// 配置项双语标签表 / Bilingual config option label registry.
// 单一 source of truth：渠道 / 行业 / 平台 的枚举值与中英文展示名。
// Demo 卡片与浮层、Task 浮层下拉框与 tag 全部从这里取翻译，避免多处漂移。
// Single source of truth for channel / vertical / platform enums + bilingual display names.
// Demo cards / modals and Task modal dropdowns + tags all read from here.

import type { LanguageTag } from "@/lib/types";
import { state } from "@/state/store";

export type ConfigKind = "channel" | "vertical" | "platform";

// 有序选项：既给下拉框用，也便于未来扩展时保证顺序。
// Ordered options: used by custom dropdowns and keeps future additions orderly.
export interface ConfigOption {
  value: string;
  label: string;
}

interface LocalizedConfigOption {
  value: string;
  labels: Record<LanguageTag, string>;
}

const CHANNEL_OPTIONS: LocalizedConfigOption[] = [
  { value: "algorithm-ecommerce", labels: { cn: "算法电商", en: "Algorithmic Commerce" } },
  { value: "app-distribution", labels: { cn: "应用分发", en: "App Distribution" } },
  { value: "content-seeding", labels: { cn: "内容种草", en: "Content Seeding" } },
  { value: "enterprise-sales", labels: { cn: "企业销售", en: "Enterprise Sales" } },
  { value: "generic", labels: { cn: "通用渠道", en: "Generic Channel" } },
  { value: "offline-distribution", labels: { cn: "线下分销", en: "Offline Distribution" } },
  { value: "offline-experience", labels: { cn: "线下体验", en: "Offline Experience" } },
  { value: "search-ecommerce", labels: { cn: "搜索电商", en: "Search Commerce" } },
  { value: "social-ecommerce", labels: { cn: "社交电商", en: "Social Commerce" } },
];

const VERTICAL_OPTIONS: LocalizedConfigOption[] = [
  { value: "beauty-personal-care", labels: { cn: "美妆个护", en: "Beauty & Personal Care" } },
  { value: "consumer-electronics", labels: { cn: "消费电子", en: "Consumer Electronics" } },
  { value: "consumer-ai-hardware", labels: { cn: "消费 AI 硬件", en: "Consumer AI Hardware" } },
  { value: "fashion-retail", labels: { cn: "时尚零售", en: "Fashion & Retail" } },
  { value: "fmcg", labels: { cn: "快消品", en: "Consumer Packaged Goods" } },
  { value: "mobile-app", labels: { cn: "移动应用", en: "Mobile App" } },
  { value: "saas", labels: { cn: "SaaS 软件", en: "SaaS" } },
];

const PLATFORM_OPTIONS: LocalizedConfigOption[] = [
  { value: "", labels: { cn: "未设置专属平台", en: "No dedicated platform" } },
  { value: "douyin", labels: { cn: "抖音", en: "Douyin" } },
  { value: "xiaohongshu", labels: { cn: "小红书", en: "RedNote" } },
  { value: "tiktok-shop-id", labels: { cn: "TikTok Shop 印尼", en: "TikTok Shop Indonesia" } },
  { value: "tiktok-shop-sea", labels: { cn: "TikTok Shop 东南亚", en: "TikTok Shop Southeast Asia" } },
  { value: "weibo", labels: { cn: "微博", en: "Weibo" } },
  { value: "generic", labels: { cn: "通用", en: "Generic" } },
];

const OPTIONS: Record<ConfigKind, LocalizedConfigOption[]> = {
  channel: CHANNEL_OPTIONS,
  vertical: VERTICAL_OPTIONS,
  platform: PLATFORM_OPTIONS,
};

const EMPTY_LABELS: Record<LanguageTag, Record<ConfigKind, string>> = {
  cn: {
    channel: "未设置渠道",
    vertical: "未设置品类",
    platform: "未设置平台",
  },
  en: {
    channel: "No channel",
    vertical: "No vertical",
    platform: "No platform",
  },
};

const UNKNOWN_LABELS: Record<LanguageTag, Record<ConfigKind, string>> = {
  cn: {
    channel: "自定义渠道",
    vertical: "自定义品类",
    platform: "自定义平台",
  },
  en: {
    channel: "Custom channel",
    vertical: "Custom vertical",
    platform: "Custom platform",
  },
};

/**
 * 获取一类配置项的全部选项 / Return all options for a config kind.
 */
export function getConfigOptions(
  kind: ConfigKind,
  language: LanguageTag = currentConfigLanguage(),
): ConfigOption[] {
  const lang = normalizeLanguage(language);
  return OPTIONS[kind].map((option) => ({
    value: option.value,
    label: option.labels[lang],
  }));
}

/**
 * 把一个原始值翻译为当前语言标签 / Translate a raw enum value to the active language label.
 * 空值走 EMPTY_LABELS，未知值走 UNKNOWN_LABELS（保留原值在 title 里由调用方处理）。
 * Falls back to EMPTY / UNKNOWN labels so the UI never shows bare English enums.
 */
export function formatConfigLabel(
  kind: ConfigKind,
  value: unknown,
  language: LanguageTag = currentConfigLanguage(),
): string {
  const lang = normalizeLanguage(language);
  const normalized = String(value ?? "").trim();
  if (!normalized) return EMPTY_LABELS[lang][kind];
  const match = OPTIONS[kind].find((option) => option.value === normalized);
  if (match) return match.labels[lang];
  return UNKNOWN_LABELS[lang][kind];
}

// 当前配置展示语言 / Current language for config labels.
function currentConfigLanguage(): LanguageTag {
  return normalizeLanguage(state.auth?.user?.preferred_language);
}

// 规范化语言值 / Normalize language values.
function normalizeLanguage(value: unknown): LanguageTag {
  return value === "en" ? "en" : "cn";
}
